import type {
  Webhook,
  WebhookLog,
} from '../../lib/flotiq-client/types/api-response';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { PencilIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import Panel from '../Panel/Panel';

export type ParsedLinkedWebhookLogsType = Record<string, WebhookLog[]>;

interface LinkPropsProps {
  /**
   * List of webhooks linked to Co
   */
  webhooks: Webhook[];
  /**
   * Parsed logs in format webhook-id:[logs]
   */
  logs: ParsedLinkedWebhookLogsType;
  /**
   * Handle refresh button click callback
   */
  handleRefreshCallback: () => Promise<void>;
  /**
   * Handle open full log list modal callback
   */
  handleOpenWebhookLogsModal: (webhookId: string) => void;
  /**
   * Handle edit specified webhook callback
   */
  handleEditWebhook: (webhookId: string) => Promise<void>;
  /**
   * Additional container classes
   */
  additionalContainerClasses: string;
  /**
   * test id
   */
  testId: string;
}

export const LinkedWebhooks = ({
  webhooks,
  logs,
  handleOpenWebhookLogsModal,
  handleRefreshCallback,
  handleEditWebhook,
  additionalContainerClasses = '',
  testId = '',
}: LinkPropsProps) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    setIsLoading(webhooks.length === 0 || Object.keys(logs).length === 0);
  }, [logs, webhooks]);

  const statusIcon = useCallback((log?: WebhookLog) => {
    if (!log) {
      return <div className={twMerge('rounded-full h-2 w-2 bg-slate-100')} />;
    }

    return (
      <div
        className={twMerge(
          'rounded-full h-2 w-2',
          log.status === 'done' && log.response?.status !== 404
            ? 'bg-lime'
            : 'bg-red',
        )}
      />
    );
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    await handleRefreshCallback();
    setIsLoading(false);
  }, [handleRefreshCallback]);

  return (
    <Panel
      title={t('ContentForm.LinkedWebhooksLogs')}
      isCollapsable
      additionalContainerClasses={twMerge(
        'px-4 py-5',
        additionalContainerClasses,
      )}
      additionalChildrenClasses="p-0"
      additionalTitleClasses="text-lg"
      open
      {...getTestProps(testId, 'linked-webhook', 'testId')}
    >
      {!isLoading ? (
        <div
          className="flex flex-col my-3"
          {...getTestProps(testId, 'linked-webhook-list')}
        >
          {webhooks.map((webhook: Webhook) => (
            <div key={webhook.id} className="flex">
              <button
                className="w-full bg-slate-50 dark:bg-slate-900 py-3 px-2 text-left
                 my-2 border border-[#F2F2F5] dark:border-[#1E293B] rounded-md cursor-pointer flex
                  items-center gap-3 text-sm hover:bg-[#A4CCFC] dark:hover:bg-[#20293A]"
                onClick={() => handleOpenWebhookLogsModal(webhook.id)}
              >
                {statusIcon(logs?.[webhook.id]?.[0])}
                {webhook.name}
              </button>
              <div className="flex items-center justify-center">
                <button onClick={() => handleEditWebhook(webhook.id)}>
                  <PencilIcon
                    {...getTestProps(testId, 'linked-webhook-edit-webhook')}
                    className="mx-3 w-3 h-3 cursor-pointer"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full flex justify-center p-5 min-h-12">
          <Loader type="spinner-grid" size="small" />
        </div>
      )}
      <Button
        buttonColor="blueBordered"
        onClick={handleRefresh}
        disabled={isLoading}
        {...getTestProps(testId, 'linked-webhook-list-refresh-btn')}
      >
        {t('Webhooks.Refresh')}
      </Button>
    </Panel>
  );
};
